import React from 'react';
import './Localita.css';
import { LocalitaFeature, Volantino } from '../../services/api';

const getLocState = (props: LocalitaFeature['properties']): 'open' | 'closed' | 'new' => {
  if (props.loc_ck_chiusa === 1 || (props.ora_chiusura_min && props.ora_chiusura_min > 0)) return 'closed';
  if (props.ora_apertura_min && props.ora_apertura_min > 0) return 'open';
  return 'new';
};

const minutesToTime = (min: number): string => {
  if (!min || min === 0) return '--:--';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const getColorByVolNum = (d: number, opacity: number = 1): string => {
  if (d > 6) return `rgba(71, 67, 67, ${opacity})`;
  switch (d) {
    case 6: return `rgba(71, 67, 67, ${opacity})`;
    case 5: return `rgba(157, 76, 235, ${opacity})`;
    case 4: return `rgba(235, 42, 42, ${opacity})`;
    case 3: return `rgba(253, 154, 4, ${opacity})`;
    case 2: return `rgba(97, 243, 5, ${opacity})`;
    case 1: return `rgba(0, 231, 255, ${opacity})`;
    default: return `rgba(153, 153, 153, ${opacity})`;
  }
};

interface LocalitaItemProps {
  feature: LocalitaFeature;
  index: number;
  onLocalitaClick?: (feature: LocalitaFeature) => void;
}

const LocalitaItem: React.FC<LocalitaItemProps> = ({ feature, index, onLocalitaClick }) => {
  const props = feature.properties;
  const state = getLocState(props);
  const stateClass =
    state === 'open' ? 'localita-item--open' :
    state === 'closed' ? 'localita-item--closed' : '';

  return (
    <li
      key={feature.id || `loc-${index}`}
      className={`localita-item-new ${stateClass}`}
      style={{
        backgroundColor: getColorByVolNum(props.mob_vol_num || 0, 0.3),
        cursor: onLocalitaClick ? 'pointer' : undefined,
      }}
      onClick={onLocalitaClick ? () => onLocalitaClick(feature) : undefined}
    >
      <div className="localita-ordinal-wrapper">
        <span
          className="localita-ordinal-circle"
          style={{ backgroundColor: getColorByVolNum(props.mob_vol_num || 0, 1) }}
        >
          {props.mob_uscloc_order !== undefined ? props.mob_uscloc_order + 1 : index + 1}
        </span>
      </div>
      <div className="localita-info">
        <div className="localita-nome-new">{props.mob_localita || 'N/D'}</div>
        <div className="localita-comune-new">{props.mob_comune || 'N/D'}</div>
        <div className="localita-volantini">
          {props.volantini && props.volantini.length > 0 ? (
            props.volantini.map((vol: Volantino, volIndex: number) => (
              <span key={vol.mob_usclocloc_id || `vol-${volIndex}`} className="volantino-chip">
                {vol.mob_usclocloc_ck_1_foto === 1 && <svg className="volantino-chip-foto" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>}
                {(() => {
                  const nome = vol.vol_nome_produzione || vol.mob_carico_vol_nome || 'Volantino';
                  const isCustom = vol.vol_nome_produzione && vol.vol_nome_produzione !== vol.mob_carico_vol_nome;
                  const qta = vol.mob_usclocloc_ck_1_show_qta === 1 ? ` · ${vol.mob_usclocloc_carico_vol}` : '';
                  return isCustom
                    ? <span style={{color:'#dc2626', fontWeight:'bold'}}>{nome}{qta}</span>
                    : <>{nome}{qta}</>;
                })()}
              </span>
            ))
          ) : (
            <span className="volantino-chip-empty"></span>
          )}
        </div>
      </div>
      <div className={`localita-stato localita-stato--${state}`}>
        <span className="localita-stato-icon">
          {state === 'open' ? '●' : state === 'closed' ? '✓' : '○'}
        </span>
        {state !== 'new' && (
          <div className="localita-stato-times">
            <span>{minutesToTime(props.ora_apertura_min)}</span>
            {props.ora_chiusura_min > 0 && (
              <span>{minutesToTime(props.ora_chiusura_min)}</span>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

interface WidgetProps {
  onClose: () => void;
  localitaFeatures?: LocalitaFeature[] | null;
  className?: string;
  hideChiuse?: boolean;
  onToggleHideChiuse?: () => void;
  onLocalitaClick?: (feature: LocalitaFeature) => void;
}

const Localita: React.FC<WidgetProps> = ({ onClose, localitaFeatures, className, hideChiuse = false, onToggleHideChiuse, onLocalitaClick }) => {
  const visibleFeatures = hideChiuse
    ? localitaFeatures?.filter(f => getLocState(f.properties) !== 'closed')
    : localitaFeatures;

  return (
    <div className={`widget-card localita-widget ${className || ''}`}>
      <div className="widget-header">
        <h3>Località Coinvolte</h3>
        {onToggleHideChiuse && (
          <button
            onClick={onToggleHideChiuse}
            className={`localita-filter-btn ${hideChiuse ? 'localita-filter-btn--active' : ''}`}
            title={hideChiuse ? 'Mostra tutte le zone' : 'Nascondi zone chiuse'}
          >
            {hideChiuse ? 'Tutte le zone' : 'Solo aperte'}
          </button>
        )}
        <button onClick={onClose} className="widget-close-btn" aria-label="Chiudi widget Località">
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="widget-content">
        {visibleFeatures && visibleFeatures.length > 0 ? (
          <ul className="localita-list-new">
            {visibleFeatures
              .slice()
              .sort((a, b) => (a.properties.mob_uscloc_order || 0) - (b.properties.mob_uscloc_order || 0))
              .map((feature, index) => (
                <LocalitaItem key={feature.id || `loc-${index}`} feature={feature} index={index} onLocalitaClick={onLocalitaClick} />
              ))}
          </ul>
        ) : localitaFeatures && localitaFeatures.length === 0 ? (
          <p className="widget-message info">Nessuna località specificata per questa uscita.</p>
        ) : hideChiuse && localitaFeatures && localitaFeatures.length > 0 ? (
          <p className="widget-message info">Tutte le zone sono chiuse.</p>
        ) : (
          <p className="widget-message info">Seleziona un'uscita per visualizzare le località.</p>
        )}
      </div>
    </div>
  );
};

export default Localita;
